/**
 * Shared branding constants for Neumind
 * Used by both the frontend (MUI) and mobile app (React Native Paper)
 */
export { ArticleLinks } from "./article-links.generated";
// Primary brand colors
export const BrandColors = {
    // Primary blue
    primary: {
        main: "#2563EB",
        light: "#60A5FA",
        dark: "#1E3A8A",
        contrast: "#FFFFFF",
    },
    // Secondary cyan
    secondary: {
        main: "#06B6D4",
        light: "#67E8F9",
        dark: "#0E7490",
        contrast: "#FFFFFF",
    },
    // Semantic colors
    success: {
        main: "#10B981",
        light: "#6EE7B7",
        dark: "#047857",
    },
    error: {
        main: "#EF4444",
        light: "#FCA5A5",
        dark: "#B91C1C",
    },
    warning: {
        main: "#F59E0B",
        light: "#FCD34D",
        dark: "#D97706",
    },
    info: {
        main: "#3B82F6",
        light: "#93C5FD",
        dark: "#1D4ED8",
    },
};
// Neutral colors (Slate scale)
export const NeutralColors = {
    50: "#F8FAFC",
    100: "#F1F5F9",
    200: "#E2E8F0",
    300: "#CBD5E1",
    400: "#94A3B8",
    500: "#64748B",
    600: "#475569",
    700: "#334155",
    800: "#1E293B",
    900: "#0F172A",
};
// Feature-specific colors (for app tools)
export const FeatureColors = {
    notes: "#FD767A",
    reminders: "#7F8AFF",
    memorisation: "#86D672",
    journals: "#87CEEB",
    trackers: "#FFE4C4",
    lists: "#FDB776",
    nudges: "#B576FD",
    tasks: "#1BB1E7",
    aspirations: "#E6E6FA",
    goals: "#EFE1CB",
};
// Spacing scale (in pixels)
export const Spacing = {
    none: 0,
    micro: 2,
    tiny: 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24,
    "2xl": 32,
    "3xl": 40,
    "4xl": 48,
};
// Border radius scale
export const BorderRadius = {
    none: 0,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    "2xl": 24,
    full: 9999,
};
// Typography
export const Typography = {
    fontFamily: {
        sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    fontWeight: {
        light: 300,
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    fontSize: {
        xs: 12,
        sm: 14,
        md: 16,
        lg: 18,
        xl: 20,
        "2xl": 24,
        "3xl": 30,
        "4xl": 36,
    },
};
// Accessibility
export const Accessibility = {
    minTouchTarget: 44,
    focusRingWidth: 2,
    focusRingOffset: 2,
};
// Brand assets
export const BrandAssets = {
    logoUrl: "https://assets.neumind.io/logo.png",
    logoWhiteUrl: "https://assets.neumind.io/logo-white.png",
    faviconUrl: "https://assets.neumind.io/favicon.svg",
    documents: {
        professionalOnePager: {
            filename: "neumind-for-professionals",
            title: "Neumind for Professionals",
        },
        clientOnePager: {
            filename: "neumind-for-individuals",
            title: "Neumind for Individuals",
        },
    },
};
// Product links — dashboards, support, blog
export const ProductLinks = {
    dashboard: "https://dashboard.neumind.io",
    supportHub: "https://support.neumind.io",
    blog: "https://www.neumind.io/resources/blog",
    /**
     * Public, fully-synthetic sample evidence report (Sarah M, case-manager
     * variant). Bundled in sites/www/public/sample-reports/sarah/. Used as the
     * "value preview" link in the onboarding welcome email and the mobile
     * desktop-handoff card. Link directly to the .html (a slashless dir URL hits
     * the SPA fallback on the CloudFront viewer function).
     */
    sampleReport: "https://www.neumind.io/sample-reports/sarah/sarah-case-manager.html",
};
// Contact & CTA links
export const ContactLinks = {
    email: "team@neumind.io",
    /** 30-min product demo — for prospects who haven't seen Neumind yet */
    bookDemo: "https://calendly.com/ellis-neumind/demo-call",
    /** 20-min general chat — default for broadcasts, low-commitment */
    bookShortChat: "https://calendly.com/ellis-neumind/new-meeting",
    /** 30-min support call — for hands-on help */
    bookSupportCall: "https://calendly.com/ellis-neumind/support-call",
    contactPage: "https://www.neumind.io/contact-us",
    website: "https://www.neumind.io",
};
// App store listings — import these, never retype an ID: three surfaces
// independently carried fabricated store IDs that were never live (ISS-260817-vn1p)
export const StoreLinks = {
    appStore: "https://apps.apple.com/gb/app/neumind/id6443500908",
    playStore: "https://play.google.com/store/apps/details?id=uk.co.neumind.neumind",
};
// Social media links — company accounts
export const SocialLinks = {
    facebook: "https://facebook.com/neumindApp",
    instagram: "https://instagram.com/neumindApp",
    twitter: "https://twitter.com/neumindApp",
    linkedin: "https://www.linkedin.com/company/neumind",
    youtube: "https://www.youtube.com/channel/UCVKXMhHehGFmF4h_L-o8l9Q",
    tiktok: "https://www.tiktok.com/@neumindapp",
};
// Founder social media accounts
export const FounderSocialLinks = {
    ellis: {
        linkedin: "https://www.linkedin.com/in/ellis-parry/",
        twitter: "https://twitter.com/DrEllisParry",
        instagram: "https://www.instagram.com/ellispaz/",
    },
    luke: {
        instagram: "https://www.instagram.com/luke_paza",
    },
};
// Amplification partners
export const AmplificationPartners = {
    emilia: {
        instagram: "https://www.instagram.com/emilia.science/",
        followers: "150K+",
        relationship: "Luke's colleague — teaches science together, happy to support neumind",
    },
};
// App-specific theme colors (for React Native Paper MD3 compatibility)
export const AppThemeColors = {
    light: {
        primary: "#005faf",
        onPrimary: "#ffffff",
        primaryContainer: "#d4e3ff",
        onPrimaryContainer: "#001c3a",
        secondary: "#4555b7",
        onSecondary: "#ffffff",
        secondaryContainer: "#dee0ff",
        onSecondaryContainer: "#000e5e",
        tertiary: "#006689",
        onTertiary: "#ffffff",
        tertiaryContainer: "#c3e8ff",
        onTertiaryContainer: "#001e2c",
        error: "#ba1a1a",
        onError: "#ffffff",
        errorContainer: "#ffdad6",
        onErrorContainer: "#410002",
        background: "#fdfcff",
        onBackground: "#1a1c1e",
        surface: "#fdfcff",
        onSurface: "#1a1c1e",
        surfaceVariant: "#e0e2ec",
        onSurfaceVariant: "#43474e",
        outline: "#74777f",
    },
    dark: {
        primary: "#a5c8ff",
        onPrimary: "#00315f",
        primaryContainer: "#004786",
        onPrimaryContainer: "#d4e3ff",
        secondary: "#bbc3ff",
        onSecondary: "#0e2288",
        secondaryContainer: "#2c3c9e",
        onSecondaryContainer: "#dee0ff",
        tertiary: "#78d1ff",
        onTertiary: "#003549",
        tertiaryContainer: "#004c68",
        onTertiaryContainer: "#c3e8ff",
        error: "#ffb4ab",
        onError: "#690005",
        errorContainer: "#93000a",
        onErrorContainer: "#ffdad6",
        background: "#1a1c1e",
        onBackground: "#e3e2e6",
        surface: "#1a1c1e",
        onSurface: "#e3e2e6",
        surfaceVariant: "#43474e",
        onSurfaceVariant: "#c3c6cf",
        outline: "#8d9199",
    },
};
// -----------------------------------------------------------------------
// Course domain identity colours (native-courses gradient/glyph recipe).
//
// SOURCE OF TRUTH: design-system/tokens/domain-colors.json at the repo
// root. Any change to a domain's hues, the neutral fallback, or the
// recipe constants below must land there FIRST — the values here are
// copied verbatim (never derived) and must not diverge from that file.
//
// Keys are corpus domain slugs (~/notes/neumind/corpus/domains/), reused
// verbatim by a compiled course's `topics[]` — see domain-colors.json's
// `dependency` block for the cross-subsystem coupling that implies: a
// renamed/re-cut corpus domain moves a course's colour with it.
// -----------------------------------------------------------------------
// Ten domain entries: slug -> { label, from, to, glyph }. `glyph` is
// measured against the white glyph plate every domain gradient carries
// (see CourseDomainGradientRecipe below), not against the gradient itself
// — domain-colors.json `rules.contrast` / `recipe.glyphPlate`.
export const CourseDomainColors = {
    "fatigue-energy": {
        label: "Fatigue and energy",
        from: "#FBBF24",
        to: "#F59E0B",
        glyph: "#92400E",
    },
    memory: {
        label: "Memory",
        from: "#A78BFA",
        to: "#8B5CF6",
        glyph: "#5B21B6",
    },
    attention: {
        label: "Attention",
        from: "#22D3EE",
        to: "#06B6D4",
        glyph: "#155E75",
    },
    communication: {
        label: "Communication",
        from: "#2DD4BF",
        to: "#14B8A6",
        glyph: "#115E59",
    },
    "executive-function": {
        label: "Planning and doing",
        from: "#818CF8",
        to: "#6366F1",
        glyph: "#3730A3",
    },
    "emotion-mood": {
        label: "Feelings and mood",
        from: "#F472B6",
        to: "#EC4899",
        glyph: "#9D174D",
    },
    "identity-adjustment": {
        label: "Who I am now",
        from: "#C084FC",
        to: "#A855F7",
        glyph: "#6B21A8",
    },
    participation: {
        label: "Getting back to life",
        from: "#34D399",
        to: "#10B981",
        glyph: "#065F46",
    },
    "physical-health": {
        label: "Body and health",
        from: "#FB7185",
        to: "#F43F5E",
        glyph: "#9F1239",
    },
    "adl-self-care": {
        label: "Everyday tasks",
        from: "#FB923C",
        to: "#F97316",
        glyph: "#9A3412",
    },
};
// Fallback for a `topics[]` slug not present in CourseDomainColors —
// unmapped domain, or no `topics` at all. Legible, anonymous, obviously
// unstyled by design, so a missing mapping is visible in review rather
// than silently plausible (domain-colors.json `neutral.$comment`).
export const CourseDomainNeutral = {
    label: "Unmapped domain",
    from: "#94A3B8",
    to: "#64748B",
    glyph: "#1E293B",
};
// Shared gradient recipe every domain (and the neutral fallback) renders
// through: linear-gradient({angle}, {from} 0%, {to} 55%, {primary} 100%).
// `primary` is Neumind primary-400 — every domain gradient terminates in
// it (domain-colors.json `rules.brandHold`), which is what makes many
// domains still read as one product.
export const CourseDomainGradientRecipe = {
    angle: "135deg",
    primary: "#60A5FA",
    stops: { from: "0%", to: "55%", primary: "100%" },
};
